import { useEffect } from 'react';
import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Image,
  Stack,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { useAnalytics } from '@/services/analytics';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import welcomeImage from '@/assets/images/people/build-your-messaging-audience.png';

type PeopleWelcomeProps = {
  /** The add-people flow's URL — Ember-owned until FPS-2618 lands. */
  addPeopleHref: string;
};

/**
 * The first-run welcome state, shown when the workspace has no profiles at
 * all (the unfiltered probe returned zero).
 */
export function PeopleWelcome({ addPeopleHref }: PeopleWelcomeProps) {
  const taxonomy = useProductTaxonomy();
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const canCreate = hasPermission('journeys/people/create');
  const label = taxonomy.term('people.titlePlural');

  useEffect(() => {
    void track('logs', 'emptyStateViewed', { 'Empty State': label });
  }, [track, label]);

  return (
    <Stack gap="400" alignInline="center" mt="400">
      <EmptyState variant="promotion">
        <EmptyStateTitle>Build your messaging audience</EmptyStateTitle>
        <EmptyStateDescription>
          Make every message relevant to your audience. Personalize messages, create segments, and build automations
          using real-time data.
        </EmptyStateDescription>
        <EmptyStateActions>
          <ViewerRestrictedTooltip readOnly={!canCreate}>
            {(context) => (
              <Button
                variant="primary"
                isDisabled={!canCreate}
                href={canCreate ? addPeopleHref : undefined}
                icon="chevron-right"
                iconPosition="trailing"
                onClick={() => void track('logs', 'emptyStateActionClicked', { 'Empty State': label })}
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
              >
                Add {taxonomy.term('people.plural')}
              </Button>
            )}
          </ViewerRestrictedTooltip>
        </EmptyStateActions>
      </EmptyState>

      <Box w="full" style={{ maxWidth: '889px' }}>
        <Image src={welcomeImage} alt="" />
      </Box>
    </Stack>
  );
}
